// src/utils/cn.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/Button.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var buttonVariants = {
  primary: "bg-theme-accent text-white hover:bg-theme-accent-hover focus:ring-theme-focus-ring",
  secondary: "bg-theme-bg-tertiary text-theme-text-primary hover:opacity-80 focus:ring-theme-focus-ring",
  danger: "bg-theme-error text-white hover:opacity-90 focus:ring-theme-error",
  ghost: "bg-transparent text-theme-text-secondary hover:bg-theme-bg-tertiary focus:ring-theme-focus-ring"
};
var buttonSizes = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2 text-base",
  lg: "px-6 py-3 text-lg"
};
function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      className: cn(
        "inline-flex items-center justify-center rounded-lg font-medium transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        buttonVariants[variant],
        buttonSizes[size],
        className
      ),
      disabled: disabled || loading,
      ...props,
      children: [
        loading && /* @__PURE__ */ jsxs(
          "svg",
          {
            className: "animate-spin -ml-1 mr-2 h-4 w-4",
            xmlns: "http://www.w3.org/2000/svg",
            fill: "none",
            viewBox: "0 0 24 24",
            children: [
              /* @__PURE__ */ jsx(
                "circle",
                {
                  className: "opacity-25",
                  cx: "12",
                  cy: "12",
                  r: "10",
                  stroke: "currentColor",
                  strokeWidth: "4"
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  className: "opacity-75",
                  fill: "currentColor",
                  d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                }
              )
            ]
          }
        ),
        children
      ]
    }
  );
}

// src/components/Card.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
function Card({ className, children, ...props }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      className: cn(
        "rounded-lg border border-theme-border bg-theme-bg-content text-theme-text-primary shadow-sm",
        className
      ),
      ...props,
      children
    }
  );
}
function CardHeader({ className, children, ...props }) {
  return /* @__PURE__ */ jsx2("div", { className: cn("flex flex-col space-y-1.5 p-6", className), ...props, children });
}
function CardTitle({ className, children, ...props }) {
  return /* @__PURE__ */ jsx2(
    "h3",
    {
      className: cn(
        "text-2xl font-semibold leading-none tracking-tight",
        className
      ),
      ...props,
      children
    }
  );
}
function CardContent({ className, children, ...props }) {
  return /* @__PURE__ */ jsx2("div", { className: cn("p-6 pt-0", className), ...props, children });
}
function CardFooter({ className, children, ...props }) {
  return /* @__PURE__ */ jsx2("div", { className: cn("flex items-center p-6 pt-0", className), ...props, children });
}

// src/components/Input.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function Input({
  label,
  error,
  helperText,
  className,
  id,
  ...props
}) {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, "-");
  return /* @__PURE__ */ jsxs2("div", { className: "space-y-1", children: [
    label && /* @__PURE__ */ jsx3(
      "label",
      {
        htmlFor: inputId,
        className: "block text-sm font-medium text-theme-text-secondary",
        children: label
      }
    ),
    /* @__PURE__ */ jsx3(
      "input",
      {
        id: inputId,
        className: cn(
          "block w-full rounded-md border-theme-border shadow-sm",
          "focus:border-theme-focus-ring focus:ring-theme-focus-ring sm:text-sm",
          "disabled:bg-theme-bg-secondary disabled:text-theme-text-muted",
          error && "border-theme-error text-theme-error focus:border-theme-error focus:ring-theme-error",
          className
        ),
        ...props
      }
    ),
    error && /* @__PURE__ */ jsx3("p", { className: "text-sm text-theme-error", children: error }),
    helperText && !error && /* @__PURE__ */ jsx3("p", { className: "text-sm text-theme-text-muted", children: helperText })
  ] });
}

// src/components/Modal.tsx
import { X } from "lucide-react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var modalSizes = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl"
};
function Modal({
  isOpen,
  onClose,
  title,
  size = "md",
  children,
  className
}) {
  if (!isOpen) return null;
  return /* @__PURE__ */ jsxs3("div", { className: "fixed inset-0 z-50 flex items-center justify-center", children: [
    /* @__PURE__ */ jsx4(
      "div",
      {
        className: "fixed inset-0 bg-black/50 transition-opacity",
        onClick: onClose
      }
    ),
    /* @__PURE__ */ jsxs3(
      "div",
      {
        className: cn(
          "relative transform rounded-lg bg-theme-bg-content text-left shadow-xl transition-all",
          "w-full mx-4 my-8 max-h-[calc(100vh-4rem)] flex flex-col",
          modalSizes[size],
          className
        ),
        children: [
          title && /* @__PURE__ */ jsxs3("div", { className: "flex items-center justify-between border-b border-theme-border px-6 py-4 flex-shrink-0", children: [
            /* @__PURE__ */ jsx4("h3", { className: "text-lg font-medium text-theme-text-primary", children: title }),
            /* @__PURE__ */ jsx4(
              "button",
              {
                onClick: onClose,
                className: "p-2 -m-2 text-theme-text-muted hover:text-theme-text-secondary focus:outline-none min-w-[44px] min-h-[44px] flex items-center justify-center",
                children: /* @__PURE__ */ jsx4(X, { className: "h-5 w-5" })
              }
            )
          ] }),
          /* @__PURE__ */ jsx4("div", { className: "flex-1 min-h-0", children })
        ]
      }
    )
  ] });
}

// src/components/Placeholder.tsx
import { Construction } from "lucide-react";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function Placeholder({ children, showIndicator = true }) {
  return /* @__PURE__ */ jsxs4("div", { className: "relative", children: [
    showIndicator && /* @__PURE__ */ jsxs4("div", { className: "absolute -top-2 -right-2 bg-theme-warning/20 text-theme-warning px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 shadow-sm", children: [
      /* @__PURE__ */ jsx5(Construction, { className: "w-3 h-3" }),
      "Placeholder"
    ] }),
    /* @__PURE__ */ jsx5("div", { className: "border border-theme-border bg-theme-warning/10 rounded-lg", children })
  ] });
}
function PlaceholderText({ text }) {
  return /* @__PURE__ */ jsxs4("span", { className: "inline-flex items-center px-2 py-1 rounded bg-theme-warning/20 text-theme-warning text-sm", children: [
    /* @__PURE__ */ jsx5(Construction, { className: "w-3 h-3 mr-1" }),
    text
  ] });
}

// src/components/ScrollArea.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function ScrollArea({ children, className = "" }) {
  return /* @__PURE__ */ jsx6("div", { className: `overflow-auto ${className}`, children });
}

// src/components/Toast.tsx
import { useEffect, useState } from "react";
import { X as X2, CheckCircle, AlertCircle, Info } from "lucide-react";
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
function Toast({ message, onDismiss }) {
  useEffect(() => {
    if (message.duration && message.duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(message.id);
      }, message.duration);
      return () => clearTimeout(timer);
    }
  }, [message, onDismiss]);
  const icons = {
    success: /* @__PURE__ */ jsx7(CheckCircle, { className: "w-5 h-5 text-theme-success" }),
    error: /* @__PURE__ */ jsx7(AlertCircle, { className: "w-5 h-5 text-theme-error" }),
    info: /* @__PURE__ */ jsx7(Info, { className: "w-5 h-5 text-theme-info" })
  };
  const colors = {
    success: "bg-theme-success/10 border-theme-success",
    error: "bg-theme-error/10 border-theme-error",
    info: "bg-theme-info/10 border-theme-info"
  };
  return /* @__PURE__ */ jsxs5("div", { className: `${colors[message.type]} border rounded-lg p-4 shadow-lg backdrop-blur-sm flex items-start gap-3 min-w-[300px] max-w-[500px]`, children: [
    icons[message.type],
    /* @__PURE__ */ jsxs5("div", { className: "flex-1", children: [
      /* @__PURE__ */ jsx7("h3", { className: "font-semibold text-theme-text-primary", children: message.title }),
      message.message && /* @__PURE__ */ jsx7("p", { className: "text-sm text-theme-text-muted mt-1", children: message.message })
    ] }),
    /* @__PURE__ */ jsx7(
      "button",
      {
        onClick: () => onDismiss(message.id),
        className: "text-theme-text-muted hover:text-white transition-colors",
        children: /* @__PURE__ */ jsx7(X2, { className: "w-4 h-4" })
      }
    )
  ] });
}
function ToastContainer({ messages, onDismiss }) {
  if (messages.length === 0) return null;
  return /* @__PURE__ */ jsx7("div", { className: "fixed top-4 right-4 z-50 space-y-2", children: messages.map((message) => /* @__PURE__ */ jsx7(Toast, { message, onDismiss }, message.id)) });
}
function useToast() {
  const [messages, setMessages] = useState([]);
  const showToast = (message) => {
    const id = Date.now().toString();
    setMessages((prev) => [...prev, { ...message, id }]);
  };
  const dismissToast = (id) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  };
  return {
    messages,
    showToast,
    dismissToast
  };
}

// src/components/MobileLanding.tsx
import { jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
function MobileLanding() {
  return /* @__PURE__ */ jsx8("div", { className: "min-h-screen bg-gradient-to-br from-emerald-900 via-green-900 to-teal-900 flex items-center justify-center p-6", children: /* @__PURE__ */ jsx8("div", { className: "max-w-md text-center", children: /* @__PURE__ */ jsxs6("div", { className: "bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-white/20", children: [
    /* @__PURE__ */ jsx8("div", { className: "mb-6", children: /* @__PURE__ */ jsx8(
      "svg",
      {
        className: "w-20 h-20 mx-auto text-white/90",
        fill: "none",
        stroke: "currentColor",
        viewBox: "0 0 24 24",
        children: /* @__PURE__ */ jsx8(
          "path",
          {
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: 2,
            d: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          }
        )
      }
    ) }),
    /* @__PURE__ */ jsx8("h1", { className: "text-3xl font-bold text-white mb-4", children: "Desktop Only" }),
    /* @__PURE__ */ jsx8("p", { className: "text-white/90 text-lg mb-6", children: "Smartphone Tycoon is designed for desktop devices. Please visit this page on your computer for the full experience." }),
    /* @__PURE__ */ jsx8("p", { className: "text-white/80 text-sm mb-3", children: "Copy this link and open it on your desktop browser:" }),
    /* @__PURE__ */ jsx8("div", { className: "bg-black/30 rounded-lg p-4 mb-2", children: /* @__PURE__ */ jsx8("code", { className: "text-white/95 text-sm font-mono select-all", children: "https://smartphonetycoon.com/mob" }) }),
    /* @__PURE__ */ jsx8("p", { className: "text-white/50 text-xs", children: "Click to select and copy the link above" })
  ] }) }) });
}

// src/components/RotateDevice.tsx
import { RotateCw } from "lucide-react";
import { jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
function RotateDevice() {
  return /* @__PURE__ */ jsx9("div", { className: "min-h-screen bg-gradient-to-br from-emerald-900 via-green-900 to-teal-900 flex items-center justify-center p-6", children: /* @__PURE__ */ jsx9("div", { className: "max-w-md text-center", children: /* @__PURE__ */ jsxs7("div", { className: "bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-white/20", children: [
    /* @__PURE__ */ jsx9("div", { className: "mb-6", children: /* @__PURE__ */ jsx9(RotateCw, { className: "w-20 h-20 mx-auto text-white/90 animate-pulse" }) }),
    /* @__PURE__ */ jsx9("h1", { className: "text-3xl font-bold text-white mb-4", children: "Rotate Your Device" }),
    /* @__PURE__ */ jsx9("p", { className: "text-white/90 text-lg mb-6", children: "Smartphone Tycoon is designed for landscape mode. Please rotate your tablet for the best experience." }),
    /* @__PURE__ */ jsxs7("div", { className: "flex items-center justify-center gap-4 text-white/60", children: [
      /* @__PURE__ */ jsx9("div", { className: "w-8 h-12 border-2 border-white/40 rounded-lg" }),
      /* @__PURE__ */ jsx9("span", { className: "text-2xl", children: "\u2192" }),
      /* @__PURE__ */ jsx9("div", { className: "w-16 h-10 border-2 border-white/60 rounded-lg" })
    ] })
  ] }) }) });
}

// src/business/MetricsDisplay.tsx
import { DollarSign, Zap, Heart, BarChart2 } from "lucide-react";

// src/utils/numberFormatter.ts
var abbreviations = [
  { value: 1e12, suffix: "T" },
  { value: 1e9, suffix: "B" },
  { value: 1e6, suffix: "M" },
  { value: 1e3, suffix: "K" }
];
function formatLargeNumber(value, options = {}) {
  const {
    decimalPlaces = 1,
    useAbbreviation = true,
    minimumValue = 1e3
  } = options;
  if (!useAbbreviation || Math.abs(value) < minimumValue) {
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimalPlaces
    });
  }
  for (const { value: threshold, suffix } of abbreviations) {
    if (Math.abs(value) >= threshold) {
      const abbreviated = value / threshold;
      return `${abbreviated.toFixed(decimalPlaces)}${suffix}`;
    }
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalPlaces
  });
}
function formatNumber(value, options = {}) {
  return formatLargeNumber(value, options);
}
function formatCompactNumber(value) {
  return formatLargeNumber(value, {
    decimalPlaces: 1,
    useAbbreviation: true,
    minimumValue: 1e3
  });
}
function formatDetailedNumber(value) {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}
function roundToThousandths(value) {
  return Math.round(value * 1e3) / 1e3;
}

// src/utils/currencyFormatter.ts
function formatCurrency(value, options = {}) {
  if (value === null || value === void 0 || isNaN(value) || !isFinite(value)) {
    return "$0";
  }
  const {
    currency = "USD",
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    notation = "standard",
    compactDisplay = "short",
    useGrouping = true,
    useCustomAbbreviation = false
  } = options;
  if (notation === "compact" && useCustomAbbreviation) {
    const formattedNumber = formatLargeNumber(value, {
      decimalPlaces: maximumFractionDigits || 1,
      useAbbreviation: true,
      minimumValue: 1e3
    });
    const currencySymbol = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(0).replace(/[\d.,\s]/g, "");
    return `${currencySymbol}${formattedNumber}`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
    notation,
    compactDisplay,
    useGrouping
  }).format(value);
}

// src/business/MetricsDisplay.tsx
import { jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
function MetricsDisplay({
  totalCost,
  price,
  profit,
  techScore,
  valueScore,
  userSatisfaction,
  className
}) {
  const metrics = [
    {
      label: "Cost",
      value: formatCurrency(totalCost),
      icon: DollarSign,
      color: "text-theme-text-secondary"
    },
    {
      label: "Price",
      value: formatCurrency(price),
      icon: BarChart2,
      color: "text-theme-text-secondary"
    },
    {
      label: "Profit",
      value: formatCurrency(profit),
      icon: DollarSign,
      color: profit >= 0 ? "text-theme-success" : "text-theme-error"
    },
    {
      label: "Tech Score",
      value: techScore.toString(),
      icon: Zap,
      color: "text-theme-info"
    }
  ];
  if (valueScore !== void 0) {
    metrics.push({
      label: "Value Score",
      value: valueScore.toString(),
      icon: BarChart2,
      color: "text-theme-accent"
    });
  }
  if (userSatisfaction !== void 0) {
    metrics.push({
      label: "User Score",
      value: userSatisfaction.toString(),
      icon: Heart,
      color: userSatisfaction >= 85 ? "text-theme-success" : userSatisfaction >= 70 ? "text-theme-warning" : "text-theme-error"
    });
  }
  return /* @__PURE__ */ jsx10(Card, { className, children: /* @__PURE__ */ jsx10(CardContent, { className: "p-6", children: /* @__PURE__ */ jsx10("div", { className: `grid gap-6 ${metrics.length <= 4 ? "grid-cols-4" : "grid-cols-5"}`, children: metrics.map((metric) => {
    const IconComponent = metric.icon;
    return /* @__PURE__ */ jsxs8("div", { children: [
      /* @__PURE__ */ jsxs8("div", { className: "flex items-center gap-2 text-theme-text-muted mb-1", children: [
        /* @__PURE__ */ jsx10(IconComponent, { className: "w-4 h-4" }),
        /* @__PURE__ */ jsx10("span", { className: "text-sm", children: metric.label })
      ] }),
      /* @__PURE__ */ jsx10("p", { className: `text-lg font-semibold ${metric.color}`, children: metric.value })
    ] }, metric.label);
  }) }) }) });
}

// src/utils/nameData.ts
var companyAdjectives = [
  "Nova",
  "Ultra",
  "Apex",
  "Prime",
  "Elite",
  "Omega",
  "Vector",
  "Fusion",
  "Global",
  "Future",
  "Zenith",
  "Swift",
  "Rapid",
  "Smart",
  "Quick",
  "Bright",
  "Sharp",
  "Fast",
  "Bold",
  "Pure",
  "Blue",
  "Red",
  "Tech"
];
var companyNouns = [
  "Wave",
  "Core",
  "Edge",
  "Pulse",
  "Force",
  "Link",
  "Beam",
  "Flow",
  "Node",
  "Flux",
  "Grid",
  "Sync",
  "Path",
  "Zone",
  "Gate",
  "Tech",
  "Labs",
  "Works",
  "Byte",
  "Data",
  "Code",
  "Net",
  "Web",
  "Hub"
];
var phoneAdjectives = [
  "Pro",
  "Max",
  "Neo",
  "Prime",
  "Nova",
  "Apex",
  "Alpha",
  "Zero",
  "Swift",
  "Bold",
  "Smart",
  "Quick",
  "Pure",
  "Fast",
  "Sharp",
  "Red",
  "Blue",
  "Gold"
];
var phoneNouns = [
  "Edge",
  "Wave",
  "Core",
  "Flow",
  "Link",
  "Beam",
  "Touch",
  "View",
  "Fold",
  "Flip",
  "Note",
  "Pad",
  "Air",
  "One",
  "Go",
  "Zen",
  "Fox",
  "Sky"
];
var phoneSuffixes = [
  "Pro",
  "Max",
  "Plus",
  "X",
  "S",
  "Neo",
  "One",
  "Go",
  "Air",
  "5G",
  "SE",
  "XR",
  "Z",
  "V",
  "T",
  "R",
  "M",
  "L"
];

// src/utils/nameGenerator.ts
function generatePhoneName() {
  let name;
  let attempts = 0;
  do {
    const adj = phoneAdjectives[Math.floor(Math.random() * phoneAdjectives.length)];
    const noun = phoneNouns[Math.floor(Math.random() * phoneNouns.length)];
    if (Math.random() < 0.6) {
      const suffix = phoneSuffixes[Math.floor(Math.random() * phoneSuffixes.length)];
      name = `${adj} ${noun} ${suffix}`;
    } else {
      name = `${adj} ${noun}`;
    }
    attempts++;
  } while (name.length >= 10 && attempts < 20);
  if (name.length >= 10) {
    const noun = phoneNouns[Math.floor(Math.random() * phoneNouns.length)];
    const suffix = phoneSuffixes[Math.floor(Math.random() * phoneSuffixes.length)];
    name = `${noun} ${suffix}`;
  }
  return name;
}
function generateCompanyName() {
  let name;
  let attempts = 0;
  do {
    const adj = companyAdjectives[Math.floor(Math.random() * companyAdjectives.length)];
    const noun = companyNouns[Math.floor(Math.random() * companyNouns.length)];
    name = `${adj} ${noun}`;
    attempts++;
  } while (name.length >= 12 && attempts < 20);
  if (name.length >= 12) {
    const noun = companyNouns[Math.floor(Math.random() * companyNouns.length)];
    name = noun;
  }
  return name;
}

// src/sections/BasicSection.tsx
import { Shuffle } from "lucide-react";
import { jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
function BasicSection({ name, setName, price, setPrice, isOpen, styles = {} }) {
  if (!isOpen) return null;
  return /* @__PURE__ */ jsxs9("div", { className: cn("space-y-4 mt-4", styles.container), children: [
    /* @__PURE__ */ jsxs9("div", { className: cn("flex items-center gap-2", styles.nameContainer), children: [
      /* @__PURE__ */ jsxs9("div", { className: cn("flex-1", styles.labelContainer), children: [
        /* @__PURE__ */ jsx11("label", { htmlFor: "name", className: cn("block text-sm font-medium text-theme-text-secondary", styles.label), children: "Device Name" }),
        /* @__PURE__ */ jsx11(
          "input",
          {
            type: "text",
            id: "name",
            "data-testid": "product-name",
            value: name,
            onChange: (e) => setName(e.target.value),
            className: cn("mt-1 w-full px-3 py-2 border border-theme-border rounded-md shadow-sm focus:ring-theme-focus-ring focus:border-theme-focus-ring text-gray-900 bg-white", styles.input)
          }
        )
      ] }),
      /* @__PURE__ */ jsx11(
        "button",
        {
          onClick: () => setName(generatePhoneName()),
          title: "Generate random name",
          className: cn("mt-6 p-2 bg-theme-accent text-white rounded-md hover:bg-theme-accent-hover transition-colors", styles.button),
          children: /* @__PURE__ */ jsx11(Shuffle, { className: cn("w-5 h-5", styles.buttonIcon) })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs9("div", { children: [
      /* @__PURE__ */ jsx11("label", { htmlFor: "price", className: cn("block text-sm font-medium text-theme-text-secondary", styles.label), children: "Price ($)" }),
      /* @__PURE__ */ jsx11(
        "input",
        {
          type: "number",
          id: "price",
          "data-testid": "product-price",
          value: price,
          onChange: (e) => setPrice(Number(e.target.value)),
          min: "0",
          step: "1",
          className: "mt-1 w-full px-3 py-2 border border-theme-border rounded-md shadow-sm focus:ring-theme-focus-ring focus:border-theme-focus-ring text-gray-900 bg-white"
        }
      )
    ] })
  ] });
}

// src/sections/DesignSection.tsx
import { Smartphone, Settings, Image as Image2 } from "lucide-react";

// src/sections/SubSection.tsx
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
function SubSection({ title, icon, children }) {
  return /* @__PURE__ */ jsxs10("div", { className: "space-y-4", children: [
    /* @__PURE__ */ jsxs10("div", { className: "flex items-center gap-2", children: [
      icon,
      /* @__PURE__ */ jsx12("h3", { className: "font-medium text-theme-text-secondary", children: title })
    ] }),
    children
  ] });
}

// src/components/LogoControls.tsx
import { useState as useState2 } from "react";
import { Image, Wand2, X as X3, Move, RotateCw as RotateCw2, ZoomIn } from "lucide-react";
import { jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
function LogoControls({ logo, setLogo }) {
  const [isGenerating, setIsGenerating] = useState2(false);
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogo({
          ...logo,
          type: "image",
          url: event.target?.result
        });
      };
      reader.readAsDataURL(file);
    }
  };
  const handleGenerateAI = async () => {
    setIsGenerating(true);
    setTimeout(() => {
      setLogo({
        ...logo,
        type: "ai",
        color: "#" + Math.floor(Math.random() * 16777215).toString(16)
      });
      setIsGenerating(false);
    }, 1500);
  };
  return /* @__PURE__ */ jsxs11("div", { className: "space-y-4", children: [
    /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx13(Image, { className: "w-5 h-5 text-theme-accent" }),
      /* @__PURE__ */ jsx13("h3", { className: "font-medium text-theme-text-secondary", children: "Logo" })
    ] }),
    /* @__PURE__ */ jsxs11("div", { className: "flex flex-wrap gap-2", children: [
      /* @__PURE__ */ jsxs11("label", { className: "flex items-center gap-2 px-4 py-2 bg-theme-bg-content border border-theme-border rounded-theme cursor-pointer hover:border-theme-accent", children: [
        /* @__PURE__ */ jsx13(Image, { className: "w-4 h-4 text-theme-accent" }),
        /* @__PURE__ */ jsx13("span", { className: "text-sm text-theme-text-secondary", children: "Upload Image" }),
        /* @__PURE__ */ jsx13(
          "input",
          {
            type: "file",
            accept: "image/*",
            onChange: handleImageUpload,
            className: "hidden"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs11(
        "button",
        {
          onClick: handleGenerateAI,
          disabled: isGenerating,
          className: "flex items-center gap-2 px-4 py-2 bg-theme-bg-content border border-theme-border rounded-theme hover:border-theme-accent",
          children: [
            /* @__PURE__ */ jsx13(Wand2, { className: "w-4 h-4 text-theme-accent" }),
            /* @__PURE__ */ jsx13("span", { className: "text-sm text-theme-text-secondary", children: isGenerating ? "Generating..." : "Generate AI Logo" })
          ]
        }
      ),
      logo.type !== "none" && /* @__PURE__ */ jsxs11(
        "button",
        {
          onClick: () => setLogo({ ...logo, type: "none" }),
          className: "flex items-center gap-2 px-4 py-2 bg-theme-bg-content border border-theme-border rounded-theme hover:border-theme-error",
          children: [
            /* @__PURE__ */ jsx13(X3, { className: "w-4 h-4 text-theme-error" }),
            /* @__PURE__ */ jsx13("span", { className: "text-sm text-theme-text-secondary", children: "Remove Logo" })
          ]
        }
      )
    ] }),
    logo.type !== "none" && /* @__PURE__ */ jsxs11("div", { className: "space-y-3", children: [
      /* @__PURE__ */ jsxs11("div", { children: [
        /* @__PURE__ */ jsxs11("label", { className: "flex items-center gap-2 text-sm text-theme-text-secondary", children: [
          /* @__PURE__ */ jsx13(Move, { className: "w-4 h-4" }),
          "Position X"
        ] }),
        /* @__PURE__ */ jsx13(
          "input",
          {
            type: "range",
            min: "-0.8",
            max: "0.8",
            step: "0.1",
            value: logo.position.x,
            onChange: (e) => setLogo({
              ...logo,
              position: {
                ...logo.position,
                x: parseFloat(e.target.value)
              }
            }),
            className: "w-full"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs11("div", { children: [
        /* @__PURE__ */ jsxs11("label", { className: "flex items-center gap-2 text-sm text-theme-text-secondary", children: [
          /* @__PURE__ */ jsx13(Move, { className: "w-4 h-4" }),
          "Position Y"
        ] }),
        /* @__PURE__ */ jsx13(
          "input",
          {
            type: "range",
            min: "-0.8",
            max: "0.8",
            step: "0.1",
            value: logo.position.y,
            onChange: (e) => setLogo({
              ...logo,
              position: {
                ...logo.position,
                y: parseFloat(e.target.value)
              }
            }),
            className: "w-full"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs11("div", { children: [
        /* @__PURE__ */ jsxs11("label", { className: "flex items-center gap-2 text-sm text-theme-text-secondary", children: [
          /* @__PURE__ */ jsx13(RotateCw2, { className: "w-4 h-4" }),
          "Rotation"
        ] }),
        /* @__PURE__ */ jsx13(
          "input",
          {
            type: "range",
            min: "0",
            max: Math.PI * 2,
            step: Math.PI / 8,
            value: logo.rotation,
            onChange: (e) => setLogo({
              ...logo,
              rotation: parseFloat(e.target.value)
            }),
            className: "w-full"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs11("div", { children: [
        /* @__PURE__ */ jsxs11("label", { className: "flex items-center gap-2 text-sm text-theme-text-secondary", children: [
          /* @__PURE__ */ jsx13(ZoomIn, { className: "w-4 h-4" }),
          "Scale"
        ] }),
        /* @__PURE__ */ jsx13(
          "input",
          {
            type: "range",
            min: "0.1",
            max: "0.5",
            step: "0.05",
            value: logo.scale,
            onChange: (e) => setLogo({
              ...logo,
              scale: parseFloat(e.target.value)
            }),
            className: "w-full"
          }
        )
      ] })
    ] })
  ] });
}

// src/sections/DesignSection.tsx
import { cameraStyles, materialOptions } from "@smartphone-tycoon/shared-core";
import { Fragment, jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
function DesignSection({
  isOpen,
  color,
  setColor,
  material,
  setMaterial,
  buttonColor,
  setButtonColor,
  hasButtons,
  setHasButtons,
  screenBorderColor,
  setScreenBorderColor,
  cameraStyle,
  setCameraStyle,
  cameraColor,
  setCameraColor,
  cameraPosition,
  setCameraPosition,
  buttonPositions,
  setButtonPositions,
  phoneSize,
  setPhoneSize,
  screenSize,
  setScreenSize,
  cornerRadius,
  setCornerRadius,
  logo,
  setLogo,
  filteredMaterials
}) {
  if (!isOpen) return null;
  return /* @__PURE__ */ jsxs12("div", { className: "space-y-6 mt-4", children: [
    /* @__PURE__ */ jsx14(
      SubSection,
      {
        title: "Body",
        icon: /* @__PURE__ */ jsx14(Smartphone, { className: "w-4 h-4 text-theme-accent" }),
        children: /* @__PURE__ */ jsxs12("div", { className: "space-y-4", children: [
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx14("label", { className: "block text-sm font-medium text-theme-text-secondary", children: "Body Color" }),
            /* @__PURE__ */ jsx14(
              "input",
              {
                type: "color",
                value: color,
                onChange: (e) => setColor(e.target.value),
                className: "mt-1 w-full h-10 rounded cursor-pointer"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx14("label", { className: "block text-sm font-medium text-theme-text-secondary mb-2", children: "Material" }),
            /* @__PURE__ */ jsx14("div", { className: "grid grid-cols-3 gap-2", children: (filteredMaterials || materialOptions).map((option) => /* @__PURE__ */ jsx14(
              "button",
              {
                onClick: () => setMaterial(option.value),
                className: `px-4 py-2 rounded-lg text-sm transition-all ${material === option.value ? "bg-theme-accent/20 text-theme-accent ring-2 ring-theme-accent" : "bg-theme-bg-content text-theme-text-secondary border border-theme-border hover:bg-theme-bg-secondary"}`,
                children: option.name
              },
              option.value
            )) })
          ] })
        ] })
      }
    ),
    /* @__PURE__ */ jsx14(
      SubSection,
      {
        title: "Buttons",
        icon: /* @__PURE__ */ jsx14(Settings, { className: "w-4 h-4 text-theme-accent" }),
        children: /* @__PURE__ */ jsxs12("div", { className: "space-y-4", children: [
          /* @__PURE__ */ jsxs12("div", { className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsx14("label", { className: "text-sm font-medium text-theme-text-secondary", children: "Show Buttons" }),
            /* @__PURE__ */ jsx14(
              "button",
              {
                onClick: () => setHasButtons(!hasButtons),
                className: `relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${hasButtons ? "bg-theme-accent" : "bg-theme-bg-tertiary"}`,
                children: /* @__PURE__ */ jsx14(
                  "span",
                  {
                    className: `inline-block h-4 w-4 transform rounded-full bg-theme-bg-content transition-transform ${hasButtons ? "translate-x-6" : "translate-x-1"}`
                  }
                )
              }
            )
          ] }),
          hasButtons && /* @__PURE__ */ jsxs12(Fragment, { children: [
            /* @__PURE__ */ jsxs12("div", { children: [
              /* @__PURE__ */ jsx14("label", { className: "block text-sm font-medium text-theme-text-secondary", children: "Button Color" }),
              /* @__PURE__ */ jsxs12(
                "select",
                {
                  value: buttonColor,
                  onChange: (e) => setButtonColor(e.target.value),
                  className: "mt-1 block w-full px-3 py-2 border border-theme-border rounded-md shadow-sm focus:border-theme-focus-ring focus:ring-theme-focus-ring text-gray-900 bg-white",
                  children: [
                    /* @__PURE__ */ jsx14("option", { value: "match", children: "Match Body" }),
                    /* @__PURE__ */ jsx14("option", { value: "black", children: "Black" }),
                    /* @__PURE__ */ jsx14("option", { value: "silver", children: "Silver" }),
                    /* @__PURE__ */ jsx14("option", { value: "gold", children: "Gold" })
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsxs12("div", { children: [
              /* @__PURE__ */ jsx14("label", { className: "block text-sm font-medium text-theme-text-secondary", children: "Volume Button Position" }),
              /* @__PURE__ */ jsx14(
                "input",
                {
                  type: "range",
                  min: "-0.8",
                  max: "0.8",
                  step: "0.1",
                  value: buttonPositions.volume,
                  onChange: (e) => setButtonPositions({
                    ...buttonPositions,
                    volume: parseFloat(e.target.value)
                  }),
                  className: "mt-1 w-full"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs12("div", { children: [
              /* @__PURE__ */ jsx14("label", { className: "block text-sm font-medium text-theme-text-secondary", children: "Power Button Position" }),
              /* @__PURE__ */ jsx14(
                "input",
                {
                  type: "range",
                  min: "-0.8",
                  max: "0.8",
                  step: "0.1",
                  value: buttonPositions.power,
                  onChange: (e) => setButtonPositions({
                    ...buttonPositions,
                    power: parseFloat(e.target.value)
                  }),
                  className: "mt-1 w-full"
                }
              )
            ] })
          ] })
        ] })
      }
    ),
    /* @__PURE__ */ jsx14(
      SubSection,
      {
        title: "Screen",
        icon: /* @__PURE__ */ jsx14(Smartphone, { className: "w-4 h-4 text-theme-accent" }),
        children: /* @__PURE__ */ jsxs12("div", { className: "space-y-4", children: [
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx14("label", { className: "block text-sm font-medium text-theme-text-secondary", children: "Screen Border Color" }),
            /* @__PURE__ */ jsxs12(
              "select",
              {
                value: screenBorderColor,
                onChange: (e) => setScreenBorderColor(e.target.value),
                className: "mt-1 block w-full px-3 py-2 border border-theme-border rounded-md shadow-sm focus:border-theme-focus-ring focus:ring-theme-focus-ring text-gray-900 bg-white",
                children: [
                  /* @__PURE__ */ jsx14("option", { value: "match", children: "Match Body" }),
                  /* @__PURE__ */ jsx14("option", { value: "black", children: "Black" }),
                  /* @__PURE__ */ jsx14("option", { value: "white", children: "White" })
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsxs12("label", { className: "block text-sm font-medium text-theme-text-secondary", children: [
              "Screen Width (",
              screenSize.width,
              "%)"
            ] }),
            /* @__PURE__ */ jsx14(
              "input",
              {
                type: "range",
                min: "80",
                max: "98",
                step: "1",
                value: screenSize.width,
                onChange: (e) => setScreenSize({
                  ...screenSize,
                  width: parseInt(e.target.value)
                }),
                className: "mt-1 w-full"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsxs12("label", { className: "block text-sm font-medium text-theme-text-secondary", children: [
              "Screen Height (",
              screenSize.height,
              "%)"
            ] }),
            /* @__PURE__ */ jsx14(
              "input",
              {
                type: "range",
                min: "85",
                max: "98",
                step: "1",
                value: screenSize.height,
                onChange: (e) => setScreenSize({
                  ...screenSize,
                  height: parseInt(e.target.value)
                }),
                className: "mt-1 w-full"
              }
            )
          ] })
        ] })
      }
    ),
    /* @__PURE__ */ jsx14(
      SubSection,
      {
        title: "Camera",
        icon: /* @__PURE__ */ jsx14(Smartphone, { className: "w-4 h-4 text-theme-accent" }),
        children: /* @__PURE__ */ jsxs12("div", { className: "space-y-4", children: [
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx14("label", { className: "block text-sm font-medium text-theme-text-secondary mb-2", children: "Camera Style" }),
            /* @__PURE__ */ jsx14("div", { className: "grid grid-cols-3 gap-2", children: cameraStyles.map((style) => /* @__PURE__ */ jsx14(
              "button",
              {
                onClick: () => setCameraStyle(style.value),
                className: `px-4 py-2 rounded-lg text-sm transition-all ${cameraStyle === style.value ? "bg-theme-accent/20 text-theme-accent ring-2 ring-theme-accent" : "bg-theme-bg-content text-theme-text-secondary border border-theme-border hover:bg-theme-bg-secondary"}`,
                children: style.name
              },
              style.value
            )) })
          ] }),
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx14("label", { className: "block text-sm font-medium text-theme-text-secondary", children: "Camera Color" }),
            /* @__PURE__ */ jsxs12(
              "select",
              {
                value: cameraColor,
                onChange: (e) => setCameraColor(e.target.value),
                className: "mt-1 block w-full px-3 py-2 border border-theme-border rounded-md shadow-sm focus:border-theme-focus-ring focus:ring-theme-focus-ring text-gray-900 bg-white",
                children: [
                  /* @__PURE__ */ jsx14("option", { value: "match", children: "Match Body" }),
                  /* @__PURE__ */ jsx14("option", { value: "black", children: "Black" }),
                  /* @__PURE__ */ jsx14("option", { value: "silver", children: "Silver" }),
                  /* @__PURE__ */ jsx14("option", { value: "gold", children: "Gold" })
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx14("label", { className: "block text-sm font-medium text-theme-text-secondary", children: "Camera Position X" }),
            /* @__PURE__ */ jsx14(
              "input",
              {
                type: "range",
                min: "-0.5",
                max: "0.5",
                step: "0.01",
                value: cameraPosition.x,
                onChange: (e) => setCameraPosition({
                  ...cameraPosition,
                  x: parseFloat(e.target.value)
                }),
                className: "mt-1 w-full"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx14("label", { className: "block text-sm font-medium text-theme-text-secondary", children: "Camera Position Y" }),
            /* @__PURE__ */ jsx14(
              "input",
              {
                type: "range",
                min: "-1",
                max: "1",
                step: "0.01",
                value: cameraPosition.y,
                onChange: (e) => setCameraPosition({
                  ...cameraPosition,
                  y: parseFloat(e.target.value)
                }),
                className: "mt-1 w-full"
              }
            )
          ] })
        ] })
      }
    ),
    /* @__PURE__ */ jsx14(
      SubSection,
      {
        title: "Logo",
        icon: /* @__PURE__ */ jsx14(Image2, { className: "w-4 h-4 text-theme-accent" }),
        children: /* @__PURE__ */ jsx14(LogoControls, { logo, setLogo })
      }
    )
  ] });
}

// src/sections/HardwareSection.tsx
import { components } from "@smartphone-tycoon/shared-core";
import { Cpu, Smartphone as Smartphone2, HardDrive, Wifi, CircuitBoard, MonitorSmartphone, Battery, Camera } from "lucide-react";
import { jsx as jsx15, jsxs as jsxs13 } from "react/jsx-runtime";
function HardwareSection({ isOpen, getComponentSetter, getSelectedValue, filteredComponents }) {
  if (!isOpen) return null;
  const categories = filteredComponents || components;
  const getIcon = (iconName) => {
    const icons = {
      Cpu: /* @__PURE__ */ jsx15(Cpu, { className: "w-5 h-5 text-theme-accent" }),
      Smartphone: /* @__PURE__ */ jsx15(Smartphone2, { className: "w-5 h-5 text-theme-accent" }),
      HardDrive: /* @__PURE__ */ jsx15(HardDrive, { className: "w-5 h-5 text-theme-accent" }),
      Wifi: /* @__PURE__ */ jsx15(Wifi, { className: "w-5 h-5 text-theme-accent" }),
      CircuitBoard: /* @__PURE__ */ jsx15(CircuitBoard, { className: "w-5 h-5 text-theme-accent" }),
      MonitorSmartphone: /* @__PURE__ */ jsx15(MonitorSmartphone, { className: "w-5 h-5 text-theme-accent" }),
      Battery: /* @__PURE__ */ jsx15(Battery, { className: "w-5 h-5 text-theme-accent" }),
      Camera: /* @__PURE__ */ jsx15(Camera, { className: "w-5 h-5 text-theme-accent" })
    };
    return icons[iconName] || /* @__PURE__ */ jsx15(Smartphone2, { className: "w-5 h-5 text-theme-accent" });
  };
  return /* @__PURE__ */ jsx15("div", { className: "space-y-6", children: categories.map((category) => {
    if (category.options.length === 0) {
      return null;
    }
    return /* @__PURE__ */ jsx15(
      SubSection,
      {
        title: category.name,
        icon: getIcon(category.icon.name || "Smartphone"),
        children: /* @__PURE__ */ jsx15("div", { className: "grid grid-cols-1 gap-2", children: [...category.options].sort((a, b) => a.cost - b.cost).map((option) => /* @__PURE__ */ jsxs13(
          "button",
          {
            "data-testid": `component-${category.id}-${option.value}`,
            onClick: () => {
              const setter = getComponentSetter(category.id);
              if (setter) setter(option.value);
            },
            className: `text-left px-4 py-3 rounded-lg transition-colors ${getSelectedValue(category.id) === option.value ? "bg-theme-accent/10 ring-2 ring-theme-accent" : "hover:bg-theme-bg-secondary"}`,
            children: [
              /* @__PURE__ */ jsx15("div", { className: "font-medium text-theme-text-primary", children: option.name }),
              option.specs && /* @__PURE__ */ jsx15("div", { className: "text-sm text-theme-text-muted", children: option.specs }),
              /* @__PURE__ */ jsxs13("div", { className: "mt-1 flex items-center gap-4", children: [
                /* @__PURE__ */ jsxs13("span", { className: "text-sm text-theme-text-muted", children: [
                  "Cost: ",
                  formatCurrency(option.cost)
                ] }),
                /* @__PURE__ */ jsxs13("span", { className: "text-sm text-theme-text-muted", children: [
                  "Score: ",
                  option.techScore
                ] })
              ] })
            ]
          },
          option.value
        )) })
      },
      category.id
    );
  }) });
}

// src/sections/SectionHeader.tsx
import { ChevronDown, ChevronRight } from "lucide-react";
import { jsx as jsx16, jsxs as jsxs14 } from "react/jsx-runtime";
function SectionHeader({ title, icon, isOpen, onToggle }) {
  return /* @__PURE__ */ jsxs14(
    "button",
    {
      onClick: onToggle,
      className: "w-full flex items-center justify-between p-2 hover:bg-theme-bg-secondary rounded-lg transition-colors",
      "data-testid": `section-${title.toLowerCase()}`,
      children: [
        /* @__PURE__ */ jsxs14("div", { className: "flex items-center gap-2", children: [
          icon,
          /* @__PURE__ */ jsx16("h2", { className: "text-xl font-semibold text-theme-text-primary", children: title })
        ] }),
        isOpen ? /* @__PURE__ */ jsx16(ChevronDown, { className: "w-5 h-5 text-theme-text-muted" }) : /* @__PURE__ */ jsx16(ChevronRight, { className: "w-5 h-5 text-theme-text-muted" })
      ]
    }
  );
}

// src/components/ErrorBoundary.tsx
import { Component } from "react";
import { jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
var ErrorBoundary = class extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return /* @__PURE__ */ jsx17("div", { className: "min-h-screen bg-theme-bg-primary text-theme-text-primary flex items-center justify-center p-4", children: /* @__PURE__ */ jsxs15("div", { className: "max-w-2xl w-full bg-theme-bg-secondary rounded-lg p-8", children: [
        /* @__PURE__ */ jsx17("h1", { className: "text-2xl font-bold text-theme-error mb-4", children: "Something went wrong" }),
        /* @__PURE__ */ jsx17("pre", { className: "bg-theme-bg-primary p-4 rounded text-sm overflow-auto mb-4", children: this.state.error?.toString() }),
        /* @__PURE__ */ jsx17(
          "button",
          {
            onClick: () => window.location.reload(),
            className: "px-4 py-2 bg-theme-accent hover:bg-theme-accent-hover rounded transition-colors",
            children: "Reload Page"
          }
        )
      ] }) });
    }
    return this.props.children;
  }
};
export {
  BasicSection,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  DesignSection,
  ErrorBoundary,
  HardwareSection,
  Input,
  LogoControls,
  MetricsDisplay,
  MobileLanding,
  Modal,
  Placeholder,
  PlaceholderText,
  RotateDevice,
  ScrollArea,
  SectionHeader,
  SubSection,
  Toast,
  ToastContainer,
  cn,
  formatCompactNumber,
  formatCurrency,
  formatDetailedNumber,
  formatLargeNumber,
  formatNumber,
  generateCompanyName,
  generatePhoneName,
  roundToThousandths,
  useToast
};
//# sourceMappingURL=index.js.map